import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { useQueryClient } from '@tanstack/react-query'

import useApi from '../../hooks/useApi'
import useTranslation from '../../hooks/useTranslation'
import { useStore } from '../pageView/Page'
import { evaluateCondition } from '../../../../common/model/condition/evaluate'
import { icon } from '../../styles/icons'

import PdfPager from '../pdf/PdfPager'
import DocumentPreview from './DocumentPreview'
import ExtractionPanel from './ExtractionPanel'

/*
 * The documents behind a record, one at a time, with a navigator above them.
 *
 * Two kinds of document: the one the system generates from the record (the PDF
 * an invoice shows), and files uploaded from outside — a supplier's invoice, a
 * receipt — kept in the record's `attachments`. Either, both, or neither can
 * exist, so the navigator lists only what is there and shows its tabs only when
 * there is more than one document to choose between.
 *
 * Section meta:
 *   generated  { condition } — when the record has a generated document
 *   folder     storage folder for uploads
 *   accept     file types the upload offers
 *   ocr        true to OCR every upload (POST /file/ocr) — task 38.02. The text
 *              is made in the background and kept on the file record.
 *   extract    'expense' to also read each upload into a suggested expense
 *              (task 38.03), in the org's language. Needs `ocr`.
 *
 * A draft takes a new upload into the form like any other edit, saved with the
 * record. A booked record is locked, so the upload is written straight away; the
 * entity has to declare `attachments` in `editableWhenLocked` for that to pass.
 */

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.form.border};
`

const Tabs = styled.div`
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  overflow-x: auto;
`

const Tab = styled.button`
  background: none;
  border: none;
  border-bottom: 3px solid transparent;
  padding: 0.5rem 0.75rem;
  margin-bottom: -1px;
  color: ${({ theme }) => theme.font.label};
  font-weight: 500;
  white-space: nowrap;
  max-width: 14rem;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;

  &.active {
    color: ${({ theme }) => theme.tab.navLinkColor};
    border-bottom-color: ${({ theme }) => theme.tab.navLinkColor};
  }
`

const Spacer = styled.div`
  flex: 1 1 auto;
`

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  background: none;
  border: none;
  padding: 0.5rem;
  color: ${({ theme }) => theme.font.label};
  font-size: 0.875rem;
  white-space: nowrap;
  cursor: pointer;

  svg { font-size: 1.25rem; }
  &:hover { color: ${({ theme }) => theme.tab.navLinkColor}; }
  &:disabled { cursor: default; opacity: 0.5; }
`

const DropZone = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 240px;
  padding: 1.5rem;
  border: 2px dashed ${({ theme, $dragging }) => $dragging ? theme.tab.navLinkColor : theme.form.border};
  border-radius: 8px;
  color: ${({ theme }) => theme.font.label};
  text-align: center;
  cursor: pointer;

  svg { font-size: 2rem; }
`

const ErrorMsg = styled.span`
  color: ${({ theme }) => theme.btn.danger};
  font-size: 0.8125rem;
`

const fileTypeOf = (file) => {
  if (file.type === 'application/pdf') return 'pdf'
  if (file.type?.startsWith('image/')) return 'image'
  return null
}

const DocumentNavigator = ({ meta = {}, entity }) => {
  const [state, setState] = useStore(s => s)
  const { postMeForm, putMe } = useApi()
  const { t, m } = useTranslation()
  const queryClient = useQueryClient()
  const inputRef = useRef(null)

  const [selected, setSelected] = useState(null)
  const [dragging, setDragging] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)

  const attachments = state?.attachments ?? []
  const hasGenerated = meta.generated
    ? evaluateCondition(meta.generated.condition, { values: state })
    : false
  const locked = state?.state === 'final'

  const documents = [
    ...(hasGenerated ? [{ key: 'generated', label: t('document.navigator.generated') }] : []),
    ...attachments.map((file, i) => ({
      key: file.fileUrl,
      label: file.fileName || m('document.navigator.uploaded', [i + 1]),
      file
    }))
  ]

  // Keep the selection on a document that exists; fall back to the first.
  const current = documents.find(d => d.key === selected) ?? documents[0]
  useEffect(() => {
    if (current && current.key !== selected) setSelected(current.key)
  }, [current?.key])

  const saveAttachments = async (next) => {
    if (locked && state.id) {
      const response = await putMe(entity, { attachments: next }, state.id)
      if (!response || response.error) throw new Error(response?.error || 'update failed')
      await queryClient.invalidateQueries({ queryKey: [entity] })
    }
    setState({ attachments: next })
  }

  const upload = async (file) => {
    setError(null)
    const fileType = fileTypeOf(file)
    if (!fileType) {
      setError(t('document.navigator.error.type'))
      return
    }
    setUploading(true)
    try {
      const fields = { folder: meta.folder || 'documents' }
      if (meta.ocr && meta.extract) Object.assign(fields, { extract: meta.extract })
      const response = await postMeForm(meta.ocr ? 'file/ocr' : 'file', file, fields)
      const record = response?.data?.record
      if (!record) throw new Error('upload failed')
      const attachment = {
        fileUrl: record.id,
        fileName: record.fileName || file.name,
        fileType,
        fileSize: file.size,
        uploadedAt: new Date().toISOString()
      }
      await saveAttachments([...attachments, attachment])
      setSelected(attachment.fileUrl)
    } catch {
      setError(t('document.navigator.error.upload'))
    } finally {
      setUploading(false)
    }
  }

  const remove = async () => {
    if (!current?.file) return
    setError(null)
    try {
      await saveAttachments(attachments.filter(a => a.fileUrl !== current.file.fileUrl))
    } catch {
      setError(t('document.navigator.error.remove'))
    }
  }

  const handleFiles = (files) => {
    if (files?.[0]) upload(files[0])
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragging(false)
    handleFiles(e.dataTransfer.files)
  }

  const fileInput = (
    <input
      ref={inputRef}
      type="file"
      accept={meta.accept}
      style={{ display: 'none' }}
      onChange={e => { handleFiles(e.target.files); e.target.value = '' }}
    />
  )

  if (documents.length === 0) {
    return (
      <Wrapper>
        <DropZone
          $dragging={dragging}
          onClick={() => !uploading && inputRef.current?.click()}
          onDragOver={e => { e.preventDefault(); setDragging(true) }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
        >
          {icon.upload}
          <span>{uploading ? t('document.navigator.uploading') : t('document.navigator.empty')}</span>
          {fileInput}
        </DropZone>
        {error && <ErrorMsg>{error}</ErrorMsg>}
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      <Bar>
        {documents.length > 1 ? (
          <Tabs>
            {documents.map(doc => (
              <Tab key={doc.key} type="button" title={doc.label}
                className={doc.key === current?.key ? 'active' : ''}
                onClick={() => setSelected(doc.key)}>
                {doc.label}
              </Tab>
            ))}
          </Tabs>
        ) : <Spacer />}
        {current?.file && !locked && (
          <IconButton type="button" title={t('document.navigator.remove')} onClick={remove}>
            {icon.delete}
          </IconButton>
        )}
        <IconButton type="button" disabled={uploading} onClick={() => inputRef.current?.click()}>
          {icon.upload}
          <span>{uploading ? t('document.navigator.uploading') : t('document.navigator.upload')}</span>
        </IconButton>
        {fileInput}
      </Bar>
      {error && <ErrorMsg>{error}</ErrorMsg>}
      {meta.extract && current?.file && !locked && (
        <ExtractionPanel key={current.file.fileUrl} fileId={current.file.fileUrl} kind={meta.extract} />
      )}
      {current?.key === 'generated'
        ? <PdfPager entity={entity} state={state} />
        : <DocumentPreview file={current.file} />}
    </Wrapper>
  )
}

// The navigator's parts, shared with the documents of a line (LineDocuments).
export { Wrapper, Bar, Tabs, Tab, Spacer, IconButton, DropZone, ErrorMsg, fileTypeOf }

export default DocumentNavigator
